<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Notifications',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    panelHeight: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref({
    loadingPlaceholder: 'none',
    blocksPageLoad: false,
  });
  const b_7315092382398562_blocksPageLoad = computed(() => false);
  const b_2248226175642056_style = computed(() => ({
    height: props.panelHeight || '600px',
    overflow: 'hidden',
  }));
  const b_1889980785424776_style = computed(() => ({
    overflow: 'auto',
    minHeight: 0,
  }));

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
    b_7315092382398562_blocksPageLoad: b_7315092382398562_blocksPageLoad.value,
    b_2248226175642056_style: b_2248226175642056_style.value,
    b_1889980785424776_style: b_1889980785424776_style.value,
  }));

  const $$load_items = () => {
    return $fn.value
      .fetch('list_notification', { parameters: { state: 'new' } })
      .then((notifications) =>
        _.orderBy(notifications, ['created_at'], ['desc'])
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_blocksPageLoad', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_style', nodeId: '2248226175642056' },
      { name: 'b_1889980785424776_style', nodeId: '1889980785424776' },
    ],
    loaders: { items: $$load_items },
    ux: { loadingPlaceholder: 'none', blocksPageLoad: false },
    dataRefs: {
      items,
      ux,
      b_7315092382398562_blocksPageLoad,
      b_2248226175642056_style,
      b_1889980785424776_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.panelHeight,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('panelHeight');
    }
  );

  const act_9533767784825_setData_mlcy2m00p_getActionArgs = async ($event) => {
    return {
      path: 'items',
      value: _.reject(items.value, { id: $event.scope.item.id }),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_9533767784825_request_tuzsbaxns_getActionArgs = async ($event) => {
    return {
      operation: 'update_notification',
      data: {
        id: $event.scope.item.id,
        etag: $event.scope.item.etag,
        state: 'handled',
        result_notes: null,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_6tqkifrky =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8zcmg05mj =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_6tqkifrky,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8zcmg05mj],
    event: 'reload',
  });
  const act_8697745105058001_reloadComponentData_lj7wl9bph =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8697745105058001_click_k87pr2k0o =
    new actionTypes.EventHandlerImpl({
      action: act_8697745105058001_reloadComponentData_lj7wl9bph,
      event: 'click',
      displayName: 'reloadComponentData',
    });
  const inst_evh_8697745105058001_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8697745105058001_click_k87pr2k0o],
    event: 'click',
  });
  const act_9533767784825_setData_mlcy2m00p = new actionTypes.SetDataAction({
    actionArgs: act_9533767784825_setData_mlcy2m00p_getActionArgs,
  });
  const inst_evh_9533767784825_success_mgm3y6aog =
    new actionTypes.EventHandlerImpl({
      action: act_9533767784825_setData_mlcy2m00p,
      event: 'success',
      displayName: 'setData',
    });
  const act_9533767784825_request_tuzsbaxns = new actionTypes.RequestAction({
    actionArgs: act_9533767784825_request_tuzsbaxns_getActionArgs,
    displayName: 'markRead',
    events: [inst_evh_9533767784825_success_mgm3y6aog],
  });
  const inst_evh_9533767784825_click_mgu97n2zg =
    new actionTypes.EventHandlerImpl({
      action: act_9533767784825_request_tuzsbaxns,
      event: 'click',
      displayName: 'markRead',
    });
  const inst_evh_9533767784825_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_9533767784825_click_mgu97n2zg],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_8zcmg05mj,
    inst_evh_7315092382398562_reload,
    inst_evh_8697745105058001_click_k87pr2k0o,
    inst_evh_8697745105058001_click,
    inst_evh_9533767784825_success_mgm3y6aog,
    inst_evh_9533767784825_click_mgu97n2zg,
    markRead: inst_evh_9533767784825_click_mgu97n2zg,
    inst_evh_9533767784825_click,
  };

  const evh_7315092382398562_reload_8zcmg05mj = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8zcmg05mj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8697745105058001_click_k87pr2k0o = async (event, scope) => {
    return await actions.inst_evh_8697745105058001_click_k87pr2k0o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8697745105058001_click = async (event, scope) => {
    return await actions.inst_evh_8697745105058001_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9533767784825_success_mgm3y6aog = async (event, scope) => {
    return await actions.inst_evh_9533767784825_success_mgm3y6aog.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9533767784825_click_mgu97n2zg = async (event, scope) => {
    return await actions.inst_evh_9533767784825_click_mgu97n2zg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_9533767784825_click = async (event, scope) => {
    return await actions.inst_evh_9533767784825_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    loadingPlaceholder="none"
    :blocksPageLoad="b_7315092382398562_blocksPageLoad"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="card" :style="b_2248226175642056_style">
      <div class="card-header d-flex align-items-center">
        <div>
          <span>
            {{ gettext('Notifications') }}
          </span>
          <span
            class="badge text-bg-secondary ms-2"
            v-if="items && items.length"
          >
            {{ items.length }}
          </span>
        </div>
        <div class="ms-auto"></div>
        <div>
          <ItpButton
            icon="fa6-solid:arrows-rotate"
            :tooltip="gettext('Reload')"
            :spinning="reloading"
            size="sm"
            variant="link"
            @click="evh_8697745105058001_click($event, {})"
          >
          </ItpButton>
        </div>
      </div>
      <ItpSkeleton fill v-if="!loaded"> </ItpSkeleton>
      <ul
        class="list-group list-group-flush flex-grow-1"
        v-if="items && items.length"
        :style="b_1889980785424776_style"
      >
        <li :key="item.id" class="list-group-item" v-for="item in items">
          <ItpBox>
            <UcExpiringContractNotificationCard
              :notification="item"
              v-if="item.type == 'expiring_contract'"
            >
            </UcExpiringContractNotificationCard>
            <UcInflationIndexedPriceChangeNotificationCard
              :notification="item"
              v-if="item.type == 'inflation_indexed_price_change'"
            >
            </UcInflationIndexedPriceChangeNotificationCard>
            <UcInvoiceImportNotificationCard
              :notification="item"
              v-if="item.type.startsWith('invoice:')"
            >
            </UcInvoiceImportNotificationCard>
            <button
              :aria-label="gettext('Remove')"
              :title="gettext('Remove')"
              class="close ms-auto mt-2 me-2 ps-4 align-self-start"
              @click="evh_9533767784825_click($event, { item })"
            >
              <span aria-hidden="true"> &times; </span>
            </button>
          </ItpBox>
        </li>
      </ul>
      <div class="card-body flex-grow-1" v-if="loaded && !items.length">
        <p class="card-text">
          {{ gettext('No new notifications.') }}
        </p>
      </div>
    </div>
  </ItpUsercontrol>
</template>
